// Money, number and date formatting in the deployment's region conventions
// (the `locale` and `currency` region settings). Each side binds these to its
// own view of the region config — the backend to its synchronous snapshot, the
// frontend to useRegionConfig() — and both read the US preset until a config
// loads, which is exactly the "en-US" / "USD" the CRM hard-coded before.
//
// An amount that carries its own currency (a stored deal, plan or invoice)
// passes it and keeps it; only the locale follows the region. Parsing and
// arithmetic on formatted parts (formatToParts, weekday lookups, timezone
// probes) do not belong here: they stay pinned to "en-US" so their output is
// stable across regions.

export interface FormatDefaults {
  locale: string;
  currency: string;
}

type LocaleOption = { locale?: string };

export type NumberFormatOptions = Omit<Intl.NumberFormatOptions, "style" | "currency"> & LocaleOption;

export type CurrencyFormatOptions = NumberFormatOptions & {
  /** The amount's own currency; blank falls back to the region currency. */
  currency?: string | null;
};

export type DateFormatOptions = Intl.DateTimeFormatOptions & LocaleOption;

type DateInput = Date | string | number;

const DATE: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
const TIME: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
const DATE_TIME: Intl.DateTimeFormatOptions = { ...DATE, ...TIME };
// Options that pick which fields show; without any, the helper's default shape
// applies and the rest (hourCycle, timeZone) modify it.
const FIELD_OPTIONS = [
  "weekday", "era", "year", "month", "day", "dayPeriod", "hour", "minute", "second",
  "fractionalSecondDigits", "timeZoneName", "dateStyle", "timeStyle",
] as const;

// Intl constructors are costly next to format(); tables and charts format
// thousands of cells with a handful of distinct option sets.
const MAX_CACHED = 500;
const numberFormats = new Map<string, Intl.NumberFormat>();
const dateFormats = new Map<string, Intl.DateTimeFormat>();

function cached<T>(cache: Map<string, T>, locale: string, options: object, create: () => T): T {
  const key = `${locale}|${JSON.stringify(options)}`;
  let format = cache.get(key);
  if (!format) {
    if (cache.size >= MAX_CACHED) cache.clear();
    format = create();
    cache.set(key, format);
  }
  return format;
}

function withoutLocale<T extends LocaleOption>(options: T): Omit<T, "locale"> {
  const { locale: _locale, ...rest } = options;
  return rest;
}

export interface RegionFormatters {
  /** A reusable Intl.NumberFormat in the region locale, for hot loops. */
  numberFormat(options?: Intl.NumberFormatOptions & LocaleOption): Intl.NumberFormat;
  /** A reusable Intl.DateTimeFormat in the region locale, for hot loops. */
  dateTimeFormat(options?: DateFormatOptions): Intl.DateTimeFormat;
  /** Major units: 1234.5 → "$1,234.50" (US) / "1 234,50 kr" (NO). */
  formatCurrency(amount: number, options?: CurrencyFormatOptions): string;
  /** Minor units: 123450 → "$1,234.50" (US) / "1 234,50 kr" (NO). */
  formatCents(cents: number, options?: CurrencyFormatOptions): string;
  /** 1234.5 → "1,234.5" (US) / "1 234,5" (NO). */
  formatNumber(value: number, options?: NumberFormatOptions): string;
  /** A ratio: 0.25 → "25%" (US) / "25 %" (NO). */
  formatPercent(ratio: number, options?: NumberFormatOptions): string;
  /** "Sep 29, 2026" by default; pass Intl options (timeZone included) to shape it. */
  formatDate(value: DateInput, options?: DateFormatOptions): string;
  /** "2:05 PM" by default. */
  formatTime(value: DateInput, options?: DateFormatOptions): string;
  /** "Sep 29, 2026, 2:05 PM" by default. */
  formatDateTime(value: DateInput, options?: DateFormatOptions): string;
  /** Whether the region locale tells time on a 12-hour clock (en-US) rather than 24-hour (nb-NO). */
  twelveHourClock(): boolean;
  /** A wall-clock hour (0–23) and minute: "2:30 PM" (US) / "14:30" (NO). */
  formatClock(hour: number, minute: number): string;
}

/** Formatters that read their locale and currency from `defaults` on every call. */
export function regionFormatters(defaults: () => FormatDefaults): RegionFormatters {
  const localeOf = (options: LocaleOption | undefined): string => options?.locale || defaults().locale;

  const numberFormat: RegionFormatters["numberFormat"] = (options = {}) => {
    const locale = localeOf(options);
    const intlOptions = withoutLocale(options);
    return cached(numberFormats, locale, intlOptions, () => new Intl.NumberFormat(locale, intlOptions));
  };
  const dateTimeFormat: RegionFormatters["dateTimeFormat"] = (options = {}) => {
    const locale = localeOf(options);
    const intlOptions = withoutLocale(options);
    return cached(dateFormats, locale, intlOptions, () => new Intl.DateTimeFormat(locale, intlOptions));
  };
  const formatCurrency: RegionFormatters["formatCurrency"] = (amount, options = {}) => {
    const { currency, ...rest } = options;
    return numberFormat({ ...rest, style: "currency", currency: currency || defaults().currency }).format(amount);
  };
  const dateWith = (fallback: Intl.DateTimeFormatOptions) => (value: DateInput, options?: DateFormatOptions): string => {
    const shaped = options !== undefined && FIELD_OPTIONS.some((field) => options[field] !== undefined);
    const date = value instanceof Date ? value : new Date(value);
    // Intl throws on an invalid date where toLocale* printed "Invalid Date"; keep the old behaviour.
    if (Number.isNaN(date.getTime())) return "Invalid Date";
    return dateTimeFormat(shaped ? options : { ...fallback, ...options }).format(date);
  };
  const twelveHourClock = (): boolean => dateTimeFormat({ hour: "numeric" }).resolvedOptions().hour12 === true;

  return {
    numberFormat,
    dateTimeFormat,
    formatCurrency,
    formatCents: (cents, options) => formatCurrency(cents / 100, options),
    formatNumber: (value, options) => numberFormat(options).format(value),
    formatPercent: (ratio, options) => numberFormat({ ...options, style: "percent" }).format(ratio),
    formatDate: dateWith(DATE),
    formatTime: dateWith(TIME),
    formatDateTime: dateWith(DATE_TIME),
    twelveHourClock,
    // Built by hand, not by Intl: ICU versions differ on the space before AM/PM.
    formatClock: (hour, minute) => {
      const mm = String(minute).padStart(2, "0");
      if (!twelveHourClock()) return `${String(hour).padStart(2, "0")}:${mm}`;
      return `${String(hour % 12 === 0 ? 12 : hour % 12)}:${mm} ${hour < 12 ? "AM" : "PM"}`;
    },
  };
}
